import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Calculator, Printer, Layers } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useCachedList } from "@/lib/cache";
import ComponentSelect from "@/components/ComponentSelect";

export default function PlannersPage() {
  const [tab, setTab] = useState("exploder");
  const [recipesData] = useCachedList("recipes");
  const recipes = recipesData || [];

  return (
    <div className="p-6 md:p-8" data-testid="planners-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Production Planners</h1>
        <p className="mt-1 text-sm text-zinc-500">Operational cost tools — reverse-engineer raw material needs or generate stepped production BOMs.</p>
      </div>
      <div className="mb-6 flex border border-zinc-300 max-w-xl no-print">
        <button data-testid="tab-exploder" onClick={() => setTab("exploder")}
          className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === "exploder" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
          <Calculator className="h-4 w-4" strokeWidth={1.5} /> L0 Cost Exploder
        </button>
        <button data-testid="tab-staggered" onClick={() => setTab("staggered")}
          className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === "staggered" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
          <Layers className="h-4 w-4" strokeWidth={1.5} /> Staggered Production Planner
        </button>
      </div>
      {tab === "exploder" ? <Exploder recipes={recipes} /> : <Staggered recipes={recipes} />}
    </div>
  );
}

function Exploder({ recipes }) {
  const [recipeId, setRecipeId] = useState("");
  const [qty, setQty] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const options = recipes.filter((r) => ["L2", "L3"].includes(r.level)).map((r) => ({ value: r.id, label: r.name, level: r.level, unit: r.output_unit }));
  const selected = recipes.find((r) => r.id === recipeId);

  const compute = async () => {
    if (!recipeId || !(Number(qty) > 0)) return toast.error("Select an item and enter a quantity");
    setBusy(true);
    try {
      const { data } = await api.post("/planner", { targets: [{ recipe_id: recipeId, qty: Number(qty) }] });
      setResult(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="exploder-tool">
      <div className="flex max-w-2xl flex-wrap items-end gap-3 border border-zinc-300 bg-zinc-50 p-4 no-print">
        <div className="min-w-[260px] flex-1">
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">L2 / L3 Item</label>
          <div className="mt-1"><ComponentSelect testid="exploder-select" options={options} value={recipeId} onChange={setRecipeId} /></div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Quantity ({selected?.output_unit || "unit"})</label>
          <input data-testid="exploder-qty" type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)}
            className="mt-1 block w-32 rounded-sm border border-zinc-300 px-3 py-1.5 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
        </div>
        <button data-testid="exploder-compute" onClick={compute} disabled={busy}
          className="rounded-sm bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50">
          {busy ? "Computing…" : "Explode to L0"}
        </button>
      </div>

      {result && (
        <div className="mt-6" data-testid="exploder-results">
          <div className="mb-3 flex items-baseline justify-between max-w-4xl">
            <h2 className="text-lg font-medium">L0 Raw Material Requirement — {result.targets[0]?.name} × {result.targets[0]?.qty}</h2>
            <div className="flex items-center gap-4">
              <span className="font-mono text-sm">Total cost: <span className="font-semibold" data-testid="exploder-total">₹{result.total_raw_cost.toFixed(2)}</span></span>
              <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 no-print">
                <Printer className="h-3.5 w-3.5" strokeWidth={1.5} /> Print
              </button>
            </div>
          </div>
          <table className="w-full max-w-4xl border-collapse border border-zinc-300 text-left">
            <thead>
              <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
                <th className="border-b border-zinc-300 px-3 py-2">Raw Material (L0)</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">Usable Needed</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-24">Wastage</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-32">Raw Needed</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-36">To Purchase</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Cost</th>
              </tr>
            </thead>
            <tbody>
              {result.raw_materials.map((row) => (
                <tr key={row.id} className="hover:bg-zinc-50 text-[13px]">
                  <td className="border-b border-zinc-200 px-3 py-2 font-medium">{row.name}</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">{row.base_qty.toFixed(1)} {row.base_unit}</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono text-amber-600">{row.wastage_pct}%</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">{row.raw_qty.toFixed(1)} {row.base_unit}</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono font-medium">{row.purchase_qty.toFixed(2)} {row.purchase_unit}</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono">₹{row.cost.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Staggered({ recipes }) {
  const [recipeId, setRecipeId] = useState("");
  const [qty, setQty] = useState("");
  const [overrides, setOverrides] = useState({});

  const options = recipes.filter((r) => ["L1.5", "L2", "L3"].includes(r.level)).map((r) => ({ value: r.id, label: r.name, level: r.level, unit: r.output_unit }));
  const recMap = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);
  const rootQty = Number(qty) || 0;

  const sections = useMemo(() => {
    if (!recipeId || rootQty <= 0) return [];
    const out = [];
    const walk = (rid, q, path, depth) => {
      if (depth > 8) return;
      const r = recMap[rid];
      if (!r || !r.output_qty) return;
      const scale = q / r.output_qty;
      const rows = r.components.map((c, i) => {
        const p = `${path}/${i}`;
        const computed = c.qty * scale;
        return { ...c, path: p, qty: overrides[p] !== undefined ? Number(overrides[p]) || 0 : computed, computed };
      });
      out.push({ path, id: rid, name: r.name, level: r.level, qty: q, unit: r.output_unit, rows });
      // Stop at L1 — user wants steps and calculation restricted to L1 and above; do not walk into L0 raw materials.
      rows.forEach((row) => { if (row.ref_type === "recipe" && recMap[row.ref_id]?.level !== "L1") walk(row.ref_id, row.qty, row.path, depth + 1); });
    };
    walk(recipeId, rootQty, "root", 0);
    return out;
  }, [recipeId, rootQty, recMap, overrides]);

  return (
    <div data-testid="staggered-tool">
      <div className="flex max-w-2xl flex-wrap items-end gap-3 border border-zinc-300 bg-zinc-50 p-4 no-print">
        <div className="min-w-[260px] flex-1">
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">L1.5 / L2 / L3 Item</label>
          <div className="mt-1"><ComponentSelect testid="staggered-select" options={options} value={recipeId} onChange={(v) => { setRecipeId(v); setOverrides({}); }} /></div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Target Qty ({recMap[recipeId]?.output_unit || "unit"})</label>
          <input data-testid="staggered-qty" type="number" step="any" value={qty} onChange={(e) => { setQty(e.target.value); setOverrides({}); }}
            className="mt-1 block w-32 rounded-sm border border-zinc-300 px-3 py-1.5 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
        </div>
        {sections.length > 0 && (
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <Printer className="h-4 w-4" strokeWidth={1.5} /> Print BOM
          </button>
        )}
      </div>

      {sections.length > 0 && (
        <div className="mt-6 space-y-6" data-testid="staggered-results">
          <p className="text-xs text-zinc-400 no-print">Adjust any sub-assembly quantity below — all its child ingredient requirements recalculate instantly.</p>
          {sections.map((sec, si) => (
            <div key={sec.path} className="max-w-4xl">
              <div className="mb-1.5 flex items-baseline gap-2">
                <span className="rounded-sm bg-zinc-950 px-1.5 py-0.5 text-[10px] font-semibold text-white">STEP {si + 1}</span>
                <span className="text-sm font-semibold">{sec.name}</span>
                <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">{sec.level}</span>
                <span className="font-mono text-sm text-zinc-500">— produce {sec.qty.toFixed(1)} {sec.unit}</span>
              </div>
              <table className="w-full border-collapse border border-zinc-300 text-left">
                <thead>
                  <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
                    <th className="border-b border-zinc-300 px-3 py-2">Requirement</th>
                    <th className="border-b border-l border-zinc-300 px-3 py-2 w-16">Tier</th>
                    <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-40">Qty Needed</th>
                    <th className="border-b border-l border-zinc-300 px-3 py-2 w-16">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {sec.rows.map((row) => (
                    <tr key={row.path} className="hover:bg-zinc-50 text-[13px]">
                      <td className="border-b border-zinc-200 px-3 py-2 font-medium">{row.name}</td>
                      <td className="border-b border-l border-zinc-200 px-3 py-2"><span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">{row.level}</span></td>
                      <td className="border-b border-l border-zinc-200 px-3 py-2 text-right">
                        {row.ref_type === "recipe" ? (
                          <input data-testid={`staggered-qty-input-${row.path.replace(/\//g, "-")}`} type="number" step="any" value={overrides[row.path] !== undefined ? overrides[row.path] : Number(row.computed.toFixed(1))}
                            onChange={(e) => setOverrides((p) => ({ ...p, [row.path]: e.target.value }))}
                            className="w-28 rounded-sm border border-blue-300 bg-blue-50/40 px-2 py-1 text-right font-mono text-[13px] focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600" />
                        ) : (
                          <span className="font-mono">{row.qty.toFixed(1)}</span>
                        )}
                      </td>
                      <td className="border-b border-l border-zinc-200 px-3 py-2 text-zinc-500">{row.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
